import { when } from "@legendapp/state";
import { nanoid } from "@repo/shared/utils/id";
import { generateKeyBetween } from "fractional-indexing";

import { getRouter } from "~/router";
import { blocks$ } from "~/stores/blocks";
import { user$ } from "../auth/user.store";
import { activeOrganization$ } from "../organizations/active-organization.store";
import { boards$ } from "./boards.crud";
import { lastVisitedBoardId$ } from "./last-visited-board.store";

export const createBoard = async () => {
  const organizationId = activeOrganization$.id.get() as string;
  const userId = user$.id.get();

  const boardId = nanoid();

  const allBoards = Object.values(boards$.get() ?? {});
  allBoards.sort((a, b) =>
    a.order < b.order ? -1 : a.order > b.order ? 1 : 0,
  );
  const lastOrder = allBoards.at(-1)?.order ?? null;
  const order = generateKeyBetween(lastOrder, null);

  boards$[boardId].set({
    id: boardId,
    name: "",
    description: "",
    avatar: null,
    organizationId,
    ownerId: userId,
    favorite: false,
    public: false,
    shareAccess: "anonymous",
    insights: null,
    order,
    createdAt: undefined as any,
    updatedAt: undefined as any,
  });

  // Wait for the board to be persisted on the server before navigating. The
  // `/boards/$boardId` route resolves access via a server query in
  // `beforeLoad`; navigating before the debounced create has synced would
  // race that query and surface a spurious "Board not found" screen.
  await when(boards$[boardId].createdAt);

  const router = getRouter();
  router.navigate({ to: `/boards/${boardId}` });
};

export const deleteBoard = (boardId: string) => {
  const router = getRouter();

  if (lastVisitedBoardId$.get() === boardId) {
    lastVisitedBoardId$.set(null);
  }

  if (router.matchRoute({ to: "/boards/$boardId", params: { boardId } })) {
    // Navigate to another board if one exists, otherwise go home
    const otherBoard = Object.values(boards$.get() ?? {}).find(
      (b) => b?.id && b.id !== boardId,
    );
    if (otherBoard) {
      router.navigate({
        to: "/boards/$boardId",
        params: { boardId: otherBoard.id },
      });
    } else {
      router.navigate({ to: "/boards" });
    }
  }

  boards$[boardId].delete();
};

/**
 * Reorders a board to a new position within the sorted list.
 * Uses fractional-indexing to compute a key between the two neighbors.
 */
export const reorderBoard = (
  boardId: string,
  newIndex: number,
  sortedIds: string[],
) => {
  const without = sortedIds.filter((id) => id !== boardId);
  if (newIndex < 0 || newIndex > without.length) return;
  const prevOrder =
    newIndex === 0
      ? null
      : (boards$[without[newIndex - 1]].order.get() ?? null);
  const nextOrder =
    newIndex >= without.length
      ? null
      : (boards$[without[newIndex]].order.get() ?? null);
  const newOrder = generateKeyBetween(prevOrder, nextOrder);
  boards$[boardId].order.set(newOrder);
};

export const duplicateBoard = async (boardId: string) => {
  const original = boards$[boardId].get();
  if (!original) return;

  const organizationId = activeOrganization$.id.get() as string;
  const userId = user$.id.get();
  const newBoardId = nanoid();

  const boardBlocks = Object.values(blocks$.get() ?? {}).filter(
    (b) => b?.boardId === boardId,
  );
  const blockIdMap = new Map<string, string>();
  for (const block of boardBlocks) {
    blockIdMap.set(block.id, nanoid());
  }

  const allBoards = Object.values(boards$.get() ?? {});
  allBoards.sort((a, b) =>
    a.order < b.order ? -1 : a.order > b.order ? 1 : 0,
  );
  const origIdx = allBoards.findIndex((b) => b.id === boardId);
  const nextOrder = allBoards[origIdx + 1]?.order ?? null;
  const order = generateKeyBetween(original.order, nextOrder);

  boards$[newBoardId].set({
    id: newBoardId,
    organizationId,
    ownerId: userId,
    name: original.name ? `${original.name} (copy)` : "Untitled board (copy)",
    description: original.description,
    avatar: original.avatar,
    favorite: false,
    // Reset sharing on duplicate so the cloned board doesn't accidentally
    // re-publish the original's data. Server enforces the same default;
    // mirroring it client-side avoids a brief flash of `public: true`.
    public: false,
    shareAccess: "anonymous",
    insights: original.insights ?? null,
    order,
    createdAt: undefined as any,
    updatedAt: undefined as any,
  });

  // Wait for the board to be created on the server before cloning blocks,
  // since blocks.create validates that the board exists.
  await when(boards$[newBoardId].createdAt);

  // Clone blocks into the new board
  for (const block of boardBlocks) {
    const newBlockId = blockIdMap.get(block.id) as string;
    blocks$[newBlockId].set({
      id: newBlockId,
      organizationId,
      boardId: newBoardId,
      datasetId: block.datasetId,
      name: block.name,
      avatar: block.avatar,
      description: block.description,
      displayType: block.displayType,
      graphConfig: block.graphConfig,
      columnsConfig: block.columnsConfig,
      tableConfig: block.tableConfig,
      riftConfig: block.riftConfig,
      statConfig: block.statConfig,
      hideHeader: block.hideHeader,
      favorite: false,
      public: block.public,
      x: block.x,
      y: block.y,
      w: block.w,
      h: block.h,
      createdAt: undefined as any,
      updatedAt: undefined as any,
    });
  }

  const router = getRouter();
  router.navigate({ to: `/boards/${newBoardId}` });
};
